import IResult from '../../../../shared/models/result/IResult';
import ApplicationModel from '../../../../shared/models/applications/api/ApplicationModel';

interface OrganizationReference {
    id: string;
    name: string;
}

export interface PlatformOrganizationApplicationsResult<TOrganization extends OrganizationReference> {
    organization: TOrganization;
    applications: ApplicationModel[];
    error?: string;
}

export type PlatformOrganizationLoadProgress<TOrganization extends OrganizationReference> = (
    result: PlatformOrganizationApplicationsResult<TOrganization>,
    loadedCount: number,
    totalCount: number
) => void;

type ApplicationsFetcher = (organizationId: string) => Promise<IResult<ApplicationModel[]>>;

const wait = (milliseconds: number): Promise<void> =>
    new Promise(resolve => window.setTimeout(resolve, milliseconds));

export const loadOrganizationApplicationsWithRetry = async <TOrganization extends OrganizationReference>(
    organization: TOrganization,
    fetchApplications: ApplicationsFetcher,
    maxAttempts = 3,
    waitForRetry: (milliseconds: number) => Promise<void> = wait
): Promise<PlatformOrganizationApplicationsResult<TOrganization>> => {
    let lastError = 'Unable to load applications.';
    let emptyResponseCount = 0;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        let wasEmptyResponse = false;
        try {
            const result = await fetchApplications(organization.id);
            if (result?.resultType === 'Ok') {
                const applications = result.data || [];
                if (applications.length > 0) {
                    return { organization, applications };
                }

                // One retry protects against the intermittent empty response without
                // making genuinely empty organizations wait through every backoff.
                emptyResponseCount++;
                if (emptyResponseCount >= 2 || attempt === maxAttempts) {
                    return { organization, applications: [] };
                }
                wasEmptyResponse = true;
            }

            if (result?.resultType === 'Unauthorized' || result?.resultType === 'PermissionDenied') {
                return {
                    organization,
                    applications: [],
                    error: result.errors?.[0] || 'Application access was denied.'
                };
            }

            lastError = result?.errors?.[0] || 'Unable to load applications.';
        } catch (error) {
            lastError = (error as Error)?.message || 'Unable to load applications.';
        }

        if (attempt < maxAttempts) {
            await waitForRetry(wasEmptyResponse ? 150 : attempt * 300);
        }
    }

    return { organization, applications: [], error: lastError };
};

export const loadPlatformOrganizationApplications = async <TOrganization extends OrganizationReference>(
    organizations: TOrganization[],
    fetchApplications: ApplicationsFetcher,
    concurrency = 6,
    onProgress?: PlatformOrganizationLoadProgress<TOrganization>
): Promise<Array<PlatformOrganizationApplicationsResult<TOrganization>>> => {
    const results = new Array<PlatformOrganizationApplicationsResult<TOrganization>>(organizations.length);
    let nextIndex = 0;
    let loadedCount = 0;

    const workers = Array.from({ length: Math.min(concurrency, organizations.length) }, async () => {
        while (nextIndex < organizations.length) {
            const index = nextIndex++;
            results[index] = await loadOrganizationApplicationsWithRetry(organizations[index], fetchApplications);
            loadedCount++;
            onProgress?.(results[index], loadedCount, organizations.length);
        }
    });

    await Promise.all(workers);
    return results;
};
